import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import { styled } from 'styled-components';

import { ScoreStatus } from '@sorare/core/src/__generated__/globalTypes';
import { Dot } from '@sorare/core/src/atoms/icons/Dot';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { BodyS, LabelL } from '@sorare/core/src/atoms/typography';
import { Ellipsis } from '@sorare/core/src/atoms/typography/Ellipsis';
import { Card } from '@sorare/core/src/components/card/Card';
import { Score } from '@sorare/core/src/components/scoring/Score';
import { filters } from '@sorare/core/src/lib/glossary';
import { getPositionInitials } from '@sorare/core/src/lib/positions';
import { CardBonus } from '@sorare/marketplace/src/components/card/CardBonus';

import { getLineupDisplayName } from '#src/lib/lineup/getLineupDisplayName.ts';
import { calculateScoreWithBonus } from '#src/lib/sports.ts';

import type {
  MyCard_So5Appearance,
  MyCard_anyCardInterface,
} from './__generated__/index.graphql.ts';

const CardWrapper = styled.div`
  width: calc(10 * var(--unit));
`;

type Props = {
  card: MyCard_anyCardInterface;
  appearance?: MyCard_So5Appearance;
};

export const MyCard = ({ card, appearance }: Props) => {
  const inLineup = !!appearance && appearance.so5Lineup?.id;
  return (
    <SBHorizontal key={card.slug}>
      <Horizontal>
        <CardWrapper>
          <Card card={card} width={80} />
        </CardWrapper>
        <Vertical className="text-left" gap={0.5}>
          <LabelL bold>{card.anyPlayer.displayName}</LabelL>
          <Horizontal gap={0.5}>
            <img src={card.anyTeam.pictureUrl || ''} alt="" height={16} />
            <BodyS color="var(--c-nd-600)">{card.anyTeam.shortName}</BodyS>
            <BodyS color="var(--c-nd-600)">
              <Dot size={0.5} />
            </BodyS>
            <BodyS color="var(--c-nd-600)">
              {card.anyPositions
                .map(position => getPositionInitials(position))
                .join(', ')}
            </BodyS>
          </Horizontal>
          <Vertical>
            <BodyS color={inLineup ? 'var(--c-nd-600)' : 'var(--c-red-400)'}>
              {inLineup ? (
                <Horizontal gap={0.5}>
                  <FormattedMessage
                    id="ScoutingCenter.inLineup"
                    defaultMessage="In: {lineupName}"
                    values={{
                      lineupName: (
                        <Ellipsis>
                          {getLineupDisplayName({
                            so5Leaderboard:
                              appearance?.so5Lineup?.so5Leaderboard,
                            so5Lineup: appearance?.so5Lineup,
                            managerTeam: appearance?.so5Lineup?.managerTeam,
                            addLeaguePrefix: true,
                            sport: card.anyPlayer.sport,
                          })}
                        </Ellipsis>
                      ),
                    }}
                  />
                </Horizontal>
              ) : (
                <FormattedMessage {...filters.notInLineupFilterLabel} />
              )}
            </BodyS>
            <CardBonus card={card} />
          </Vertical>
        </Vertical>
      </Horizontal>
      <Horizontal>
        <Score
          hideReviewing
          shape="PILL"
          score={
            calculateScoreWithBonus(
              parseFloat(card.power),
              card.anyPlayer.playerGameScore?.score ?? 0
            ).score
          }
          scoreStatus={
            card.anyPlayer.playerGameScore?.scoreStatus ?? ScoreStatus.FINAL
          }
          scoreType="CARD_SCORE"
          isCappedMode={false}
          isAffordable={false}
          animated={false}
          inverted={false}
        />
      </Horizontal>
    </SBHorizontal>
  );
};

MyCard.fragments = {
  anyCardInterface: gql`
    fragment MyCard_anyCardInterface on AnyCardInterface {
      slug
      anyTeam {
        slug
        shortName
        pictureUrl
      }
      power
      anyPlayer {
        slug
        displayName
        anyPositions
        sport
        playerGameScore(gameId: $gameIdWithoutPrefix) {
          id
          score
          scoreStatus
        }
      }
      ...Card_anyCard
      ...CardBonus_anyCard
    }
    ${Card.fragments.anyCard}
    ${CardBonus.fragments.anyCard}
  ` as TypedDocumentNode<MyCard_anyCardInterface>,
  appearance: gql`
    fragment MyCard_So5Appearance on So5Appearance {
      id
      so5Lineup {
        id
        managerTeam {
          id
          ...getLineupDisplayName_managerTeam
        }
        ...getLineupDisplayName_so5Lineup
        so5Leaderboard {
          slug
          ...getLineupDisplayName_so5Leaderboard
        }
      }
    }
    ${getLineupDisplayName.fragments.so5Leaderboard}
    ${getLineupDisplayName.fragments.so5Lineup}
    ${getLineupDisplayName.fragments.managerTeam}
  ` as TypedDocumentNode<MyCard_So5Appearance>,
};
